/**
 * Marketing video assets — ALL PLACEHOLDER.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ REPLACE ME. This is the only file you need to edit when the real     │
 * │ Vaanexa recordings are ready. Nothing else references video URLs.    │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * The URLs below point at Google's public sample-media bucket so the player,
 * the modal and the stage swap can all be seen working end to end. They are
 * NOT Vaanexa content and must not ship to production.
 *
 * When you have the real files:
 *   1. Drop them in `apps/website/public/product/` (and posters beside them).
 *   2. Change the paths here to `/product/<name>.mp4` and `/product/<name>.webp`.
 *   3. Delete PLACEHOLDER_NOTICE and the `isPlaceholder` flags.
 *
 * Encoding targets: H.264 MP4, ≤1600px wide, ≤2.5 MB per stage clip, no audio
 * track (these are silent UI recordings), plus a matching WebP poster at the
 * same aspect ratio so nothing shifts while the file loads.
 */

import type { JourneyStageId } from './content';

/** Flip to false once real assets are in — surfaces a dev-only console warning. */
export const PLACEHOLDER_NOTICE = true;

const SAMPLE_BUCKET = 'https://storage.googleapis.com/gtv-videos-bucket/sample';

export type VideoAsset = {
  /** Video file. Muted, silent, looped in-page. */
  src: string;
  /** Poster frame. Strongly recommended — prevents a blank box before load. */
  poster?: string;
  /** Plain-language description of what the clip shows. */
  alt: string;
  /** Intrinsic aspect ratio, used to reserve space and avoid layout shift. */
  aspect?: string;
  isPlaceholder?: boolean;
};

/**
 * The hero's "Watch Vaanexa in Action" button opens this in a modal.
 * REPLACE ME: `/product/vaanexa-overview.mp4` + `/product/vaanexa-overview.webp`
 */
export const OVERVIEW_VIDEO: VideoAsset = {
  src: `${SAMPLE_BUCKET}/ForBiggerJoyrides.mp4`,
  alt: 'Placeholder clip — replace with the Vaanexa product overview.',
  aspect: '16 / 9',
  isPlaceholder: true,
};

/**
 * One clip per stage of the scroll-controlled product story. Any stage left out
 * of this map falls back to the built-in animated UI mock-up, so you can swap
 * them in one at a time.
 *
 * REPLACE ME: `/product/<stage>.mp4` + `/product/<stage>.webp`
 */
export const STAGE_VIDEOS: Partial<Record<JourneyStageId, VideoAsset>> = {
  connect: {
    src: `${SAMPLE_BUCKET}/ForBiggerBlazes.mp4`,
    alt: 'Placeholder clip — replace with the channel-connection walkthrough.',
    aspect: '16 / 10',
    isPlaceholder: true,
  },
  knowledge: {
    src: `${SAMPLE_BUCKET}/ForBiggerEscapes.mp4`,
    alt: 'Placeholder clip — replace with the knowledge-base walkthrough.',
    aspect: '16 / 10',
    isPlaceholder: true,
  },
  ai: {
    src: `${SAMPLE_BUCKET}/ForBiggerFun.mp4`,
    alt: 'Placeholder clip — replace with the AI-reply walkthrough.',
    aspect: '16 / 10',
    isPlaceholder: true,
  },
  qualify: {
    src: `${SAMPLE_BUCKET}/ForBiggerJoyrides.mp4`,
    alt: 'Placeholder clip — replace with the lead-qualification walkthrough.',
    aspect: '16 / 10',
    isPlaceholder: true,
  },
  action: {
    src: `${SAMPLE_BUCKET}/ForBiggerMeltdowns.mp4`,
    alt: 'Placeholder clip — replace with the booking, quotation and payment walkthrough.',
    aspect: '16 / 10',
    isPlaceholder: true,
  },
  analytics: {
    src: `${SAMPLE_BUCKET}/Sintel.mp4`,
    alt: 'Placeholder clip — replace with the analytics-dashboard walkthrough.',
    aspect: '16 / 10',
    isPlaceholder: true,
  },
};

/**
 * Master switch. While false, the site renders the built-in animated mock-ups
 * and ignores STAGE_VIDEOS entirely — flip it on once the real clips land, or
 * set VITE_MARKETING_VIDEO=1 locally to preview the video path.
 */
export const USE_STAGE_VIDEOS = import.meta.env.VITE_MARKETING_VIDEO === '1';
